<template>
  <v-navigation-drawer
    v-model="drawer"
    permanent
    :width="width"
    app
  >
    <v-list density="compact">
      <v-list-item @click="expand()">
        <template #prepend>
          <v-icon>{{ expandIcon }}</v-icon>
        </template>
        <v-list-item-title>{{ app }}</v-list-item-title>
      </v-list-item>

      <v-list-item link :to="'/chatbot'">
        <template #prepend>
          <v-badge
            :content="notificationNumber"
            :model-value="notificationNumber > 0"
            color="green"
          >
            <v-icon>mdi-robot-happy</v-icon>
          </v-badge>
        </template>
        <v-list-item-title>{{ $t('messages.smartbot') }}</v-list-item-title>
      </v-list-item>

      <v-list-item
        v-for="item in items"
        :key="item.title"
        link
        :to="item.link"
      >
        <template #prepend>
          <v-icon>{{ item.icon }}</v-icon>
        </template>
        <v-list-item-title>{{ item.title }}</v-list-item-title>
      </v-list-item>
    </v-list>

    <v-divider v-if="isMaster"></v-divider>
    <v-list density="compact" v-if="isMaster">
      <v-list-item
        v-for="item in itemsAdmin"
        :key="item.title"
        link
        :to="item.link"
      >
        <template #prepend>
          <v-icon>{{ item.icon }}</v-icon>
        </template>
        <v-list-item-title>{{ item.title }}</v-list-item-title>
      </v-list-item>
    </v-list>

    <v-divider></v-divider>
    <v-list density="compact">
      <v-list-item link :to="'/user'">
        <template #prepend>
          <v-badge
            :content="notificationNumber"
            :model-value="notificationNumber > 0"
            color="green"
          >
            <v-icon>mdi-account</v-icon>
          </v-badge>
        </template>
        <v-list-item-title>{{ this.$store.state.userName }}</v-list-item-title>
      </v-list-item>

      <v-menu location="bottom">
        <template #activator="{ props }">
          <v-list-item v-bind="props">
            <template #prepend>
              <v-icon>
                <img :src="`https://flagcdn.com/w20/${$i18n.locale === 'en' ? 'gb' : $i18n.locale}.png`" style="width: 20px; height: auto;" alt="country_flag" />
              </v-icon>
            </template>
            <v-list-item-title v-if="expanded">{{ $t('messages.language') }}</v-list-item-title>
          </v-list-item>
        </template>
        <v-list>
          <v-list-item v-for="(language, code) in availableLanguages" :key="code" @click="changeLanguage(code)">
            <v-list-item-title>{{ availableLanguages[code] }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-list>

    <v-divider></v-divider>
    <v-list density="compact">
      <v-list-item @click="logout()">
        <template #prepend>
          <v-icon>mdi-logout</v-icon>
        </template>
        <v-list-item-title>{{ $t('messages.logout') }}</v-list-item-title>
      </v-list-item>
      <v-list-item link :to="`/help`">
        <template #prepend>
          <v-icon>mdi-help</v-icon>
        </template>
        <v-list-item-title>{{ $t('messages.help') }}</v-list-item-title>
      </v-list-item>
    </v-list>

  </v-navigation-drawer>
</template>

<script>
import httpClient from '../../api/httpClient'
import { hasPermission, PERMISSIONS } from '@/utils/rolePermissions'

export default {
  inject: ['bus'],
  data() {
    return {
      notificationNumber: 0,
      expandIcon: 'mdi-chevron-right',
      expanded: false,
      isAdmin: false,
      isMaster: false,
      userEmail: "",
      app: "SmartSurf",
      drawer: true,
      items: [],
      itemsAdmin: [],
      width: 55,
    }
  },
  computed: {
    availableLanguages() {
      return {
        en: this.$t('messages.lang_en'),
        pt: this.$t('messages.lang_pt'),
        fr: this.$t('messages.lang_fr'),
        de: this.$t('messages.lang_de'),
        es: this.$t('messages.lang_es')
      }
    }
  },
  mounted: function () {
    this.bus.on('sideBarLogout', () => {
      this.logout()
    })
    // Ensure language is loaded from cookie
    this.loadLanguageFromCookie()
  },
  created () {
    this.updateMenu()
    this.bus.on('loggedIn', () => {
      this.$nextTick(() => {
        if (this.$t) {
          this.updateMenu()
          this.$forceUpdate()
        }
      })
    })
  },
  watch: {
    '$store.state.profile'() {
      this.updateMenu()
    }
  },
  methods: {
    changeLanguage(code) {
      this.$i18n.locale = code
      // Save language preference in cookie for 1 year
      const expires = new Date()
      expires.setFullYear(expires.getFullYear() + 1)
      document.cookie = `smartsurf_language=${code}; expires=${expires.toUTCString()}; path=/`
    },
    loadLanguageFromCookie() {
      const cookies = document.cookie.split(';')
      for (let cookie of cookies) {
        const trimmed = cookie.trim()
        if (trimmed.startsWith('smartsurf_language=')) {
          const value = trimmed.substring('smartsurf_language='.length)
          if (['pt', 'en', 'fr', 'de', 'es'].includes(value)) {
            this.$i18n.locale = value
            return
          }
        }
      }
    },
    expand: function () {
      this.expanded = !this.expanded
      if(this.expanded) {
        this.width = 210
        this.expandIcon = 'mdi-chevron-left'
        this.bus.emit('menuMove', this.width)
      } else {
        this.width = 55
        this.expandIcon = 'mdi-chevron-right'
        this.bus.emit('menuMove', this.width)
      }
    },
    updateMenu: function () {
      if(this.$store.state.email !== null && this.$store.state.email !== "") {
        this.profile = this.$store.state.profile
        this.email = this.$store.state.email

        if (!this.$t) return;

        // Build menu items based on permissions
        this.items = []
        
        if (hasPermission(this.profile, PERMISSIONS.VIEW_CALENDAR)) {
          this.items.push({ title: this.$t('messages.calendar'), link: '/calendar', icon: 'mdi-calendar-range' })
        }
        
        if (hasPermission(this.profile, PERMISSIONS.VIEW_LESSONS)) {
          this.items.push({ title: this.$t('messages.lessons_menu'), link: '/lesson', icon: 'mdi-calendar-account' })
        }
        
        if (hasPermission(this.profile, PERMISSIONS.VIEW_STUDENTS)) {
          this.items.push({ title: this.$t('messages.students_menu'), link: '/student', icon: 'mdi-account-group' })
        }
        
        if (hasPermission(this.profile, PERMISSIONS.VIEW_STAFF)) {
          this.items.push({ title: this.$t('messages.staff_menu'), link: '/staff', icon: 'mdi-account-tie' })
        }
        
        if (hasPermission(this.profile, PERMISSIONS.VIEW_REPORTS)) {
          this.items.push({ title: this.$t('messages.dashboard_menu'), link: '/dashboard', icon: 'mdi-view-dashboard' })
        }
        
        if (hasPermission(this.profile, PERMISSIONS.MANAGE_SETTINGS)) {
          this.items.push({ title: this.$t('messages.settings_menu'), link: '/settings', icon: 'mdi-cog-outline' })
        }

        // Admin items for Master role
        this.isMaster = hasPermission(this.profile, PERMISSIONS.VIEW_ADMIN_PANEL)
        if (this.isMaster) {
          this.itemsAdmin = [
            { title: this.$t('messages.administration_menu'), link: '/admin', icon: 'mdi-brain' },
            { title: this.$t('messages.works_menu'), link: '/works', icon: 'mdi-home-modern' },
            { title: this.$t('messages.equipments_menu'), link: '/equipment', icon: 'mdi-nas' },
            { title: this.$t('messages.transports_menu'), link: '/transport', icon: 'mdi-train-car' }
          ]
        }
      }
    },
    isProfileMaster() {
      return this.profile === "Master"
    },
    logout: function () {
      const rememberedEmail = localStorage.getItem('rememberedEmail')
      localStorage.clear()
      if (rememberedEmail) {
        localStorage.setItem('rememberedEmail', rememberedEmail)
      }
      this.$store.commit('setEmail', '')
      this.$store.commit('setUserName', '')
      this.$store.commit('setProfile', '')

      httpClient
        .post('/oauth/api/v1/signout')
        .then(() => {})
        .catch(() => {})

      this.app = "SmartSurf"
      if( this.$router.currentRoute.path !== '/') {
        this.$router.push('/')
      }

      this.bus.emit('logout', '')
    }
  }
}
</script>